import React, { useEffect, useState, useContext, useMemo } from "react";
import { LiferayService } from "../../../../common/services/liferay";
import { ApplicationPropertiesContext } from "../../../../common/context/ApplicationPropertiesProvider";
import { AccessTokenContext } from "../../../../common/context/AccessTokenProvider";
import { CGE } from "../../../../common/components/CGE";
import Loading from "../../../../common/components/Loading";
import { getPathName } from "../../../../common/utils/utilsFunc";
import ClayTabs from "@clayui/tabs";
import "rc-slider/assets/index.css";
import { Range } from "rc-slider";
import createSliderWithTooltip from "rc-slider/lib/createSliderWithTooltip";
import ReactApexChart from "react-apexcharts";
import ClayButton from "@clayui/button";
import "./styles.scss";

const RangeWithTooltip = createSliderWithTooltip(Range);

// Flag para mostrar/ocultar el mensaje verde de "Datos recibidos..."
const SHOW_SERVICE_LOG = false;

// SWITCH PARA MODO HARDCODE
const USE_HARDCODED_MRID = false;
const HARDCODED_MRID = "ANZ1020110000078";

const MAX_DAYS = 40; // límite máximo de días entre fechas
const MAX_DETAILED_DAYS = 7; // hasta 7 días mostramos todos los puntos
const MAX_POINTS_PER_SERIES = 500; // tope duro de puntos por serie

// Formato requerido: "YYYY-MM-DDT00:00:00+00:00"
function toBackendDate(date) {
    const utc = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
    );
    return utc.toISOString().replace(".000Z", "+00:00");
}

// Versión fin de día: "YYYY-MM-DDT23:59:59.999+00:00"
function toBackendDateEndOfDay(date) {
    const utc = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
    );
    return utc.toISOString().replace("Z", "+00:00");
}

// Helpers fechas para inputs (últimos 24 meses)
function toYMD(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
function monthsAgo(date, n) {
    const d = new Date(date);
    const day = d.getDate();
    d.setMonth(d.getMonth() - n);
    if (d.getDate() !== day) d.setDate(0);
    return d;
}
// Sumar días a una fecha (sin mutar el original)
function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}
// Parsear "YYYY-MM-DD" a Date
function parseYMD(value) {
    if (!value) return null;
    const [y, m, d] = value.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}

/**
 * Parsea un localDateTime ignorando el offset final.
 * "2025-11-29T00:00:00-03:00" -> Date(2025,10,29,0,0,0) en local.
 */
function parseLocalDateTimeIgnoringOffset(str) {
    if (!str) return null;
    const base = str.slice(0, 19); // "YYYY-MM-DDTHH:MM:SS"
    const [datePart, timePart] = base.split("T");
    if (!datePart || !timePart) return null;

    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mm, ss] = timePart.split(":").map(Number);

    return new Date(y, m - 1, d, hh || 0, mm || 0, ss || 0);
}

/**
 * Agrupa puntos por día (YYYY-MM-DD) y calcula promedio o suma.
 * Recibe puntos con shape: { date: Date, value: number }
 */
function groupPointsByDay(points, mode = "avg") {
    const bucket = new Map();

    points.forEach((p) => {
        const baseDate = p.chartDate || p.date;
        const key = baseDate.toISOString().slice(0, 10); // YYYY-MM-DD
        if (!bucket.has(key)) {
            bucket.set(key, { sum: 0, count: 0, date: baseDate });
        }
        const acc = bucket.get(key);
        acc.sum += p.value;
        acc.count += 1;
    });

    const result = [];
    bucket.forEach((acc) => {
        const value = mode === "sum" ? acc.sum : acc.sum / acc.count;
        result.push({
            date: acc.date,
            value,
        });
    });

    result.sort((a, b) => a.date - b.date);
    return result;
}

/**
 * Downsampling simple: si hay más puntos que "max", nos quedamos
 * con uno cada "step".
 */
function downsample(points, max) {
    if (!Array.isArray(points) || points.length <= max) return points;
    const step = Math.ceil(points.length / max);
    const result = [];
    for (let i = 0; i < points.length; i += step) {
        result.push(points[i]);
    }
    return result;
}

/**
 * Extrae los IntervalBlocks como:
 * [
 *   { readingType: string, points: [{ date: Date, value: number }, ...] },
 *   ...
 * ]
 *
 * Para la GRÁFICA usamos localDateTime ignorando el offset (-03:00).
 */
function extractIntervalBlocks(rawResponse) {
    if (!rawResponse) return [];

    const created =
        rawResponse.createdMeterReadings || rawResponse.CreatedMeterReadings;
    if (!created) return [];

    const payload = created.Payload || created.payload;
    if (!payload) return [];

    const mrContainer = payload.MeterReadings || payload.meterReadings;
    if (!mrContainer) return [];

    let meterReading =
        mrContainer.MeterReading || mrContainer.meterReading || null;
    if (!meterReading) return [];

    // Puede venir como arreglo o como objeto
    if (Array.isArray(meterReading)) {
        meterReading = meterReading[0]; // por ahora tomamos el primero
    }

    const blocks =
        meterReading.IntervalBlocks || meterReading.intervalBlocks || [];
    if (!Array.isArray(blocks)) return [];

    return blocks
        .map((block) => {
            const readingTypeRaw = block.ReadingType || block.readingType || "";
            const readingType =
                typeof readingTypeRaw === "string"
                    ? readingTypeRaw.trim()
                    : String(readingTypeRaw || "").trim();

            const readings =
                block.IntervalReadings || block.intervalReadings || [];

            if (!Array.isArray(readings)) {
                return { readingType, points: [] };
            }

            const points = readings
                .filter((r) => r && r.localDateTime)
                .map((r) => {
                    const date = parseLocalDateTimeIgnoringOffset(r.localDateTime);
                    return {
                        date,
                        value: Number(r.value ?? 0),
                    };
                })
                .filter((p) => p.date && !Number.isNaN(p.date.getTime()))
                .sort((a, b) => a.date - b.date);

            return { readingType, points };
        })
        .filter((b) => b.points.length > 0);
}

/**
 * Versión de IntervalBlocks para EXCEL:
 * conserva localDateTime y timeStamp tal cual vienen del servicio.
 */
function extractIntervalBlocksForExcel(rawResponse) {
    if (!rawResponse) return [];

    const created =
        rawResponse.createdMeterReadings || rawResponse.CreatedMeterReadings;
    if (!created) return [];

    const payload = created.Payload || created.payload;
    if (!payload) return [];

    const mrContainer = payload.MeterReadings || payload.meterReadings;
    if (!mrContainer) return [];

    let meterReading =
        mrContainer.MeterReading || mrContainer.meterReading || null;
    if (!meterReading) return [];

    if (Array.isArray(meterReading)) {
        meterReading = meterReading[0];
    }

    const blocks =
        meterReading.IntervalBlocks || meterReading.intervalBlocks || [];
    if (!Array.isArray(blocks)) return [];

    return blocks.map((block) => {
        const readingTypeRaw = block.ReadingType || block.readingType || "";
        const readingType =
            typeof readingTypeRaw === "string"
                ? readingTypeRaw.trim()
                : String(readingTypeRaw || "").trim();

        const readings =
            block.IntervalReadings || block.intervalReadings || [];

        if (!Array.isArray(readings)) {
            return { readingType, readings: [] };
        }

        const normalizedReadings = readings
            .filter((r) => r && (r.localDateTime || r.timeStamp || r.timestamp))
            .map((r) => {
                const localDateTime =
                    r.localDateTime || r.localdatetime || "";
                const timeStamp = r.timeStamp || r.timestamp || "";

                const dateForFilter = localDateTime
                    ? parseLocalDateTimeIgnoringOffset(localDateTime)
                    : timeStamp
                        ? new Date(timeStamp)
                        : null;

                return {
                    localDateTime,
                    timeStamp,
                    value: Number(r.value ?? 0),
                    dateForFilter,
                };
            })
            .filter(
                (r) =>
                    r.dateForFilter &&
                    !Number.isNaN(r.dateForFilter.getTime())
            )
            .sort((a, b) => a.dateForFilter - b.dateForFilter);

        return { readingType, readings: normalizedReadings };
    });
}

/**
 * Versión "plana": junta todos los puntos de todos los IntervalBlocks
 * (por si hace falta en algún momento).
 */
function extractIntervalPoints(rawResponse) {
    const blocks = extractIntervalBlocks(rawResponse);
    if (!blocks.length) return [];
    const all = [];
    blocks.forEach((b) => {
        all.push(...b.points);
    });
    all.sort((a, b) => a.date - b.date);
    return all;
}

/**
 * Construye las series para ApexChart a partir de:
 * - IntervalBlocks (cada uno se vuelve UNA serie)
 * - fromDate (inicio seleccionado)
 * - range (offsets de días en el slider)
 * - tabs (incluye cimCodes y seriesLabels)
 *
 * Hace match ReadingType ↔ label usando:
 *  1) Mapa cimCode → label
 *  2) Fallback por índice (seriesLabels[index])
 *  3) Fallback legacy (consumida/inyectada) o genérico
 *
 * Además:
 * - Si el rango seleccionado > MAX_DETAILED_DAYS → se agrupa por día.
 * - Siempre se aplica downsampling a MAX_POINTS_PER_SERIES.
 */
function buildSeriesFromResponse(
    rawResponse,
    fromDateStr,
    range,
    tabs,
    active
) {
    if (!rawResponse) return null;

    const from = parseYMD(fromDateStr);
    if (!from) return null;

    const startOffset = Math.min(range[0], range[1]);
    const endOffset = Math.max(range[0], range[1]);

    const startDate = addDays(from, startOffset);
    const endDate = addDays(from, endOffset);
    endDate.setHours(23, 59, 59, 999);

    const totalDaysSelected = endOffset - startOffset;

    const blocks = extractIntervalBlocks(rawResponse);
    if (!blocks.length) return null;

    const currentTab = tabs[active] || tabs[0] || {};
    const cimCodes = currentTab.cimCodes || [];
    const seriesLabels = currentTab.seriesLabels || [];

    // LOGS DE DEBUG: configuración de la pestaña y bloques
    console.log("[AMI-DETAIL] buildSeriesFromResponse - currentTab:", currentTab);
    console.log("[AMI-DETAIL] buildSeriesFromResponse - cimCodes:", cimCodes);
    console.log("[AMI-DETAIL] buildSeriesFromResponse - seriesLabels:", seriesLabels);
    console.log(
        "[AMI-DETAIL] buildSeriesFromResponse - blocks readingType:",
        blocks.map((b) => b.readingType)
    );

    // Mapa cimCode -> label (por posición)
    const labelByReadingType = {};
    cimCodes.forEach((code, idx) => {
        if (!code) return;
        const normalized = String(code).trim();
        const label =
            (seriesLabels && seriesLabels[idx]) ||
            `${currentTab.label || "Serie"} ${idx + 1}`;
        labelByReadingType[normalized] = label;
    });

    console.log(
        "[AMI-DETAIL] buildSeriesFromResponse - labelByReadingType:",
        labelByReadingType
    );

    const series = blocks
        .map((block, index) => {
            const filteredPoints = block.points.filter(
                (p) => p.date >= startDate && p.date <= endDate
            );

            if (!filteredPoints.length) {
                return null;
            }

            let effectivePoints = filteredPoints;

            // Si el rango es grande, agrupamos por día
            if (totalDaysSelected > MAX_DETAILED_DAYS) {
                effectivePoints = groupPointsByDay(filteredPoints, "avg");
            }

            // Downsampling de seguridad
            effectivePoints = downsample(
                effectivePoints,
                MAX_POINTS_PER_SERIES
            );

            if (!effectivePoints.length) {
                return null;
            }

            const data = effectivePoints.map((p) => ({
                x: p.date.getTime(),
                y: p.value,
            }));

            // 1) Intentamos por ReadingType exacto (cimCode)
            const rt = (block.readingType || "").trim();
            let name = labelByReadingType[rt];

            console.log(
                "[AMI-DETAIL] Serie",
                index,
                "- readingType:",
                rt,
                "- nameByRT:",
                name,
                "- fallbackLabelIndex:",
                seriesLabels[index]
            );

            // 2) Fallback por índice si no encontramos por ReadingType
            if (!name && seriesLabels && seriesLabels[index]) {
                name = seriesLabels[index];
            }

            // 3) Fallback legacy consumo / inyección si son 2 series
            if (!name && blocks.length === 2) {
                name = index === 0 ? "Energía consumida" : "Energía inyectada";
            }

            // 4) Fallback final genérico
            if (!name) {
                name =
                    block.readingType ||
                    `${currentTab.label || "Serie"} ${index + 1}`;
            }

            console.log("[AMI-DETAIL] Serie", index, "- nombre final:", name);

            return {
                name,
                data,
            };
        })
        .filter(Boolean);

    if (!series.length) return null;

    // --- NUEVO: ordenar Fase 1, Fase 2, Fase 3 en ese orden ---
    const faseOrder = { "Fase 1": 1, "Fase 2": 2, "Fase 3": 3 };
    series.sort((a, b) => {
        const ao = faseOrder[a.name] ?? 99;
        const bo = faseOrder[b.name] ?? 99;
        if (ao !== bo) return ao - bo;
        return a.name.localeCompare(b.name);
    });
    // -----------------------------------------------------------

    return series;
}

// === COMPONENTE PRINCIPAL ===
export default function AmiDetail({ selected, numeroCliente, onBack }) {
    const properties = useContext(ApplicationPropertiesContext);
    const { accessToken } = useContext(AccessTokenContext);

    const [active, setActive] = useState(0);

    // Rango del slider, se calculará dinámicamente según las fechas elegidas
    const [range, setRange] = useState([0, 0]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rawResponse, setRawResponse] = useState(null);

    // Indicador de que ya se hizo al menos una consulta válida
    const [hasConsulted, setHasConsulted] = useState(false);

    // El usuario modificó las fechas manualmente (custom range)
    const [userChangedDates, setUserChangedDates] = useState(false);

    // Tooltip “¿Qué es la energía activa?”
    const [isHintVisible, setIsHintVisible] = useState(false);

    // Usamos una sola "hoy" fija en el ciclo de vida del componente
    const today = useMemo(() => new Date(), []);
    const minSelectable = useMemo(
        () => monthsAgo(today, 24),
        [today]
    ); // hoy - 24 meses

    const [fromDate, setFromDate] = useState(""); // "Fecha desde"
    const [toDate, setToDate] = useState(""); // "Fecha hasta"

    // Cuando cambian las fechas POR EL USUARIO, reseteamos la consulta/response
    useEffect(() => {
        if (!userChangedDates) {
            return; // no tocar nada cuando el cambio fue programático (initRangeFromSelected)
        }
        setHasConsulted(false);
        setRawResponse(null);
        setError(null);
    }, [fromDate, toDate, userChangedDates]);

    // Labels leídos desde properties con fallback
    const labels = useMemo(() => {
        return {
            title: properties?.ami_detail_title || "Detalle del medidor",
            backButton:
                properties?.ami_detail_back_button || "Regresar",

            loading:
                properties?.ami_detail_loading ||
                "Cargando variables del medidor...",
            error:
                properties?.ami_detail_error ||
                "No fue posible obtener las variables del medidor.",

            dateRangeTitle:
                properties?.ami_detail_date_range_title ||
                "Rango de fechas",
            dateRangeHelp:
                properties?.ami_detail_date_range_help ||
                "Arrastra para ver datos entre fechas seleccionadas",

            dateFromLabel:
                properties?.ami_detail_date_from_label ||
                "Fecha desde",
            dateToLabel:
                properties?.ami_detail_date_to_label || "Fecha hasta",

            // instrucción sobre el rango
            dateRangeInstruction:
                properties?.ami_detail_date_range_instruction ||
                "Seleccione un rango de fechas (máximo 40 días)",

            sliderLeftLabel:
                properties?.ami_detail_slider_left_label ||
                `${MAX_DAYS} días atrás`,
            sliderRightLabel:
                properties?.ami_detail_slider_right_label || "Hoy",

            daysSelectedSingular:
                properties?.ami_detail_days_selected_singular ||
                "día seleccionado",
            daysSelectedPlural:
                properties?.ami_detail_days_selected_plural ||
                "días seleccionados",

            chartTitle:
                properties?.ami_detail_chart_title ||
                "Energía consumida e inyectada",
            chartHintPrefix:
                properties?.ami_detail_chart_hint_prefix ||
                "¿Qué es la ",
            chartHintSuffix:
                properties?.ami_detail_chart_hint_suffix || "?",

            // mensaje editable cuando no hay datos
            noDataMessage:
                properties?.ami_detail_no_data_message ||
                "No se encontraron datos en el rango seleccionado. Intente con otras fechas.",
        };
    }, [properties]);

    // Tabs configurables desde properties (incluyendo tooltip, CIM codes, labels y rangos Y)
    const tabs = useMemo(() => {
        const totalTabsRaw = properties?.ami_detail_tabs_total;
        let totalTabs = parseInt(totalTabsRaw, 10);
        if (Number.isNaN(totalTabs) || totalTabs <= 0) {
            totalTabs = 4; // fallback
        }

        const result = [];
        for (let i = 1; i <= totalTabs; i++) {
            const key =
                properties?.[`ami_detail_tab_${i}_key`] || `tab${i}`;
            const label =
                properties?.[`ami_detail_tab_${i}_label`] ||
                `Tab ${i}`;
            const metricName =
                properties?.[`ami_detail_tab_${i}_metric`] ||
                label.toLowerCase();
            const tooltip =
                properties?.[`ami_detail_tab_${i}_tooltip`] || "";

            // CIM codes: coma-separados en el .properties
            const cimRaw =
                properties?.[`ami_detail_tab_${i}_cimcodes`];
            const cimCodes = cimRaw
                ? cimRaw
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [];

            // Labels por serie: coma-separados
            const labelsRaw =
                properties?.[`ami_detail_tab_${i}_labels`];
            const seriesLabels = labelsRaw
                ? labelsRaw
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [];

            // Rango Y configurado (opcional)
            const yMinRaw = properties?.[`ami_detail_tab_${i}_y_min`];
            const yMaxRaw = properties?.[`ami_detail_tab_${i}_y_max`];

            let yMin = null;
            let yMax = null;

            if (
                yMinRaw !== undefined &&
                yMinRaw !== null &&
                `${yMinRaw}`.trim() !== ""
            ) {
                const n = Number(yMinRaw);
                if (!Number.isNaN(n)) yMin = n;
            }

            if (
                yMaxRaw !== undefined &&
                yMaxRaw !== null &&
                `${yMaxRaw}`.trim() !== ""
            ) {
                const n = Number(yMaxRaw);
                if (!Number.isNaN(n)) yMax = n;
            }

            // --- NUEVO: etiqueta del eje Y configurable / con fallback ---
            const yUnitProp = properties?.[`ami_detail_tab_${i}_y_unit_label`];
            let defaultYUnit = "";
            const labelLower = (label || "").toLowerCase();

            if (labelLower.includes("activa")) {
                defaultYUnit = "Kwh";
            } else if (labelLower.includes("reactiva")) {
                defaultYUnit = "Kvarh";
            } else if (labelLower.includes("volt")) {
                defaultYUnit = "Volt";
            } else if (labelLower.includes("corriente")) {
                defaultYUnit = "A";
            } else if (labelLower.includes("potencia")) {
                defaultYUnit = "Kw";
            }

            const yUnitLabel =
                typeof yUnitProp === "string" && yUnitProp.trim() !== ""
                    ? yUnitProp.trim()
                    : defaultYUnit;
            // -----------------------------------------------------------

            result.push({
                key,
                label,
                metricName,
                tooltip,
                cimCodes,
                seriesLabels,
                yMin,
                yMax,
                yUnitLabel, // NUEVO
            });
        }
        return result;
    }, [properties]);

    // Total de días entre fromDate y toDate (máx. 40)
    const totalDays = useMemo(() => {
        const from = parseYMD(fromDate);
        const to = parseYMD(toDate);
        if (!from || !to) return 0;

        const ONE_DAY = 1000 * 60 * 60 * 24;
        let diff = Math.round((to.getTime() - from.getTime()) / ONE_DAY);
        if (diff < 0) diff = 0;
        if (diff > MAX_DAYS) diff = MAX_DAYS; // seguridad
        return diff;
    }, [fromDate, toDate]);

    const hasDateRange = !!(fromDate && toDate);
    // Consideramos válido también el caso de mismo día (diff === 0)
    const hasValidRange = hasDateRange;

    // Cuando cambian las fechas, ajustamos el slider para cubrir todo el rango seleccionado
    useEffect(() => {
        if (hasValidRange) {
            setRange([0, totalDays]);
        } else {
            setRange([0, 0]);
        }
    }, [hasValidRange, totalDays]);

    // Serie construida desde el response real (si lo hay)
    const series = useMemo(() => {
        if (
            !hasValidRange ||
            !fromDate ||
            !tabs.length ||
            !rawResponse
        ) {
            return [];
        }

        const srvSeries = buildSeriesFromResponse(
            rawResponse,
            fromDate,
            range,
            tabs,
            active
        );

        return srvSeries || [];
    }, [
        rawResponse,
        fromDate,
        range,
        tabs,
        active,
        hasValidRange,
    ]);

    // Min y max dinámicos para el eje Y (solo para fallback si no hay config)
    const { minY, maxY } = useMemo(() => {
        let min = Infinity;
        let max = -Infinity;

        series.forEach((s) => {
            (s.data || []).forEach((pt) => {
                const y = typeof pt.y === "number" ? pt.y : Number(pt.y);
                if (Number.isNaN(y)) return;
                if (y < min) min = y;
                if (y > max) max = y;
            });
        });

        if (!series.length || !Number.isFinite(min) || !Number.isFinite(max)) {
            return { minY: null, maxY: null };
        }

        return { minY: min, maxY: max };
    }, [series]);

    const currentTab = tabs[active] || tabs[0] || null;
    const chartHintMetric =
        currentTab?.metricName || currentTab?.label || "";
    const chartHintTooltip = currentTab?.tooltip || "";

    // Opciones de Apex (zoom/pan activos + rangos Y configurables)
    const options = useMemo(
        () => {
            // Config desde tabs si existe
            const configuredMin =
                currentTab && typeof currentTab.yMin === "number"
                    ? currentTab.yMin
                    : null;
            const configuredMax =
                currentTab && typeof currentTab.yMax === "number"
                    ? currentTab.yMax
                    : null;

            const baseFormatter = (val) => {
                if (val === undefined || val === null || Number.isNaN(val)) {
                    return "";
                }
                const num = Number(val);
                // más precisión para rangos pequeños
                const range =
                    configuredMin !== null && configuredMax !== null
                        ? configuredMax - configuredMin
                        : maxY !== null && minY !== null
                            ? maxY - minY
                            : null;

                const decimals =
                    range !== null && range <= 1 ? 3 : 2;

                return num.toFixed(decimals);
            };

            let yaxisConfig = {
                labels: {
                    show: true,
                    formatter: baseFormatter,
                },
            };

            if (
                configuredMin !== null &&
                configuredMax !== null &&
                configuredMin < configuredMax
            ) {
                // Usamos el rango configurado por pestaña
                yaxisConfig = {
                    min: configuredMin,
                    max: configuredMax,
                    tickAmount: 5,
                    labels: {
                        show: true,
                        formatter: baseFormatter,
                    },
                };
            } else if (
                minY != null &&
                maxY != null &&
                Number.isFinite(minY) &&
                Number.isFinite(maxY) &&
                minY < maxY
            ) {
                // Fallback: auto-cálculo por datos
                if (minY >= 0 && maxY <= 1) {
                    yaxisConfig = {
                        min: 0,
                        max: 1,
                        tickAmount: 5,
                        labels: {
                            show: true,
                            formatter: baseFormatter,
                        },
                    };
                } else {
                    const diff = maxY - minY || 1;
                    const padding = diff * 0.05;
                    let min = minY - padding;
                    let max = maxY + padding;

                    if (min < 0 && minY >= 0) {
                        min = 0;
                    }

                    yaxisConfig = {
                        min,
                        max,
                        tickAmount: 6,
                        labels: {
                            show: true,
                            formatter: baseFormatter,
                        },
                    };
                }
            }

            // --- NUEVO: título del eje Y con unidad configurable ---
            const yAxisTitle =
                (currentTab && currentTab.yUnitLabel) ? currentTab.yUnitLabel : "";
            const finalYAxisConfig = {
                ...yaxisConfig,
                title: {
                    text: yAxisTitle,
                },
            };
            // -------------------------------------------------------

            return {
                chart: {
                    type: "line",
                    zoom: {
                        enabled: true,
                        type: "x",
                        autoScaleYaxis: true,
                    },
                    toolbar: {
                        show: true,
                        tools: {
                            zoom: true,
                            zoomin: true,
                            zoomout: true,
                            pan: true,
                            reset: true,
                        },
                    },
                },
                stroke: { curve: "smooth", width: 2 },
                markers: {
                    size:
                        series &&
                        series.length &&
                        series[0].data.length > 300
                            ? 0
                            : 3,
                    strokeWidth: 2,
                    hover: { sizeOffset: 1 },
                },
                grid: {
                    borderColor: "#dcdcdc",
                    row: {
                        colors: ["#f7f8f9", "transparent"],
                        opacity: 0.6,
                    },
                },
                xaxis: {
                    type: "datetime",
                    labels: {
                        show: true,
                        // clave para que NO los trate como UTC
                        datetimeUTC: false,
                    },
                    tooltip: { enabled: false },
                },
                yaxis: finalYAxisConfig,
                tooltip: {
                    x: { show: true },
                },
                legend: { show: true },
                // Colores distintos para múltiples líneas (trifásicos, etc.)
                colors: [
                    "#4c5a67",
                    "#0b5fff",
                    "#f3b21a",
                    "#2e7d32",
                    "#9c27b0",
                    "#ff7043",
                ],
            };
        },
        [minY, maxY, series, currentTab]
    );

    // Tooltip del slider: convertir índice a fecha calendario en función de fromDate
    const tipFormatter = (v) => {
        const from = parseYMD(fromDate);
        if (!from) return "";
        const d = new Date(from);
        d.setDate(from.getDate() + v);
        return d.toLocaleDateString("es-CL", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
        });
    };

    const daysSelected = range[1] - range[0];

    // Cálculo dinámico del máximo para "Fecha hasta"
    const maxToDate = useMemo(() => {
        const hardMax = today;
        if (!fromDate) {
            return hardMax;
        }

        const from = parseYMD(fromDate);
        if (!from) {
            return hardMax;
        }

        let plus40 = addDays(from, MAX_DAYS);
        if (plus40 > hardMax) {
            plus40 = hardMax;
        }

        return plus40;
    }, [fromDate, today]);

    // Configuración de columnas para el Excel (6 columnas: agregamos Variable)
    const amiExcelTitles = useMemo(() => {
        const pathName = getPathName();
        return {
            [pathName]: [
                { label: "Medidor", key: "medidor" },
                { label: "Tipo de energía", key: "tipoEnergia" },
                { label: "Variable", key: "variable" },
                { label: "Fecha lectura", key: "fechaLectura" },
                { label: "Fecha local Chile", key: "fechaLocalChile" },
                { label: "Valor", key: "valor" },
            ],
        };
    }, []);

    // Filas a exportar en Excel (mismo rango de fechas que la gráfica)
    const excelRows = useMemo(() => {
        if (
            !hasValidRange ||
            !fromDate ||
            !tabs.length ||
            !rawResponse
        ) {
            return [];
        }

        const from = parseYMD(fromDate);
        if (!from) return [];

        const startOffset = Math.min(range[0], range[1]);
        const endOffset = Math.max(range[0], range[1]);

        const startDate = addDays(from, startOffset);
        const endDate = addDays(from, endOffset);
        endDate.setHours(23, 59, 59, 999);

        const blocks = extractIntervalBlocksForExcel(rawResponse);
        if (!blocks.length) return [];

        const current = tabs[active] || tabs[0] || {};
        const energyType = current.label || "—";
        const cimCodes = current.cimCodes || [];
        const seriesLabels = current.seriesLabels || [];
        const meterId =
            (USE_HARDCODED_MRID ? HARDCODED_MRID : selected?.serial) || "";

        // Mapa cimCode -> label (igual que en buildSeriesFromResponse)
        const labelByReadingType = {};
        cimCodes.forEach((code, idx) => {
            if (!code) return;
            const normalized = String(code).trim();
            const label =
                (seriesLabels && seriesLabels[idx]) ||
                `${current.label || "Serie"} ${idx + 1}`;
            labelByReadingType[normalized] = label;
        });

        const rows = [];

        blocks.forEach((block, index) => {
            // Determinamos el nombre de la variable (serie) para este bloque
            const rt = (block.readingType || "").trim();
            let variableName = labelByReadingType[rt];

            if (!variableName && seriesLabels && seriesLabels[index]) {
                variableName = seriesLabels[index];
            }

            if (!variableName && blocks.length === 2) {
                variableName =
                    index === 0 ? "Energía consumida" : "Energía inyectada";
            }

            if (!variableName) {
                variableName =
                    block.readingType ||
                    `${current.label || "Serie"} ${index + 1}`;
            }

            const filtered = block.readings.filter(
                (r) =>
                    r.dateForFilter >= startDate &&
                    r.dateForFilter <= endDate
            );

            filtered.forEach((r) => {
                rows.push({
                    medidor: meterId,
                    tipoEnergia: energyType,
                    variable: variableName,
                    // aquí van TAL CUAL del servicio
                    fechaLectura: r.timeStamp || "",
                    fechaLocalChile: r.localDateTime || "",
                    valor: r.value,
                });
            });
        });

        // opcional: ordenar por fecha (como string ISO está bien)
        rows.sort((a, b) =>
            (a.fechaLectura || "").localeCompare(b.fechaLectura || "")
        );

        return rows;
    }, [
        rawResponse,
        fromDate,
        range,
        tabs,
        active,
        hasValidRange,
        selected,
    ]);

    const excelButtonProps = useMemo(
        () => ({
            auxArray: excelRows,
            arTitles: amiExcelTitles,
            disabled: !excelRows || excelRows.length === 0,
        }),
        [excelRows, amiExcelTitles]
    );

    const sliderLeftText = useMemo(() => {
        if (!hasValidRange) return labels.sliderLeftLabel;
        if (totalDays === 1) return "1 día atrás";
        if (totalDays === 0) return "Mismo día";
        return `${totalDays} días atrás`;
    }, [hasValidRange, totalDays, labels.sliderLeftLabel]);

    const sliderRightText = useMemo(() => {
        if (!hasValidRange || !toDate) return labels.sliderRightLabel;
        const to = parseYMD(toDate);
        if (!to) return labels.sliderRightLabel;
        return to.toLocaleDateString("es-CL", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
        });
    }, [hasValidRange, toDate, labels.sliderRightLabel]);

    const canConsult =
        hasValidRange && !!selected && !!accessToken && !!tabs.length;

    /**
     * Lógica interna de consulta (no valida canConsult, se asume válido).
     * Recibe opcionalmente tabIndex para usar los cimCodes de esa pestaña.
     */
    const doConsult = (from, to, source = "consulta interna", tabIndex) => {
        if (!from || !to) return;
        if (!selected || !accessToken || !tabs.length) return;

        const idx = typeof tabIndex === "number" ? tabIndex : active;
        const currentTab = tabs[idx] || tabs[0];
        const tabCimCodes = currentTab?.cimCodes || [];

        const dynamicMRID = selected?.serial;
        const mRID = USE_HARDCODED_MRID
            ? HARDCODED_MRID
            : dynamicMRID || HARDCODED_MRID;

        if (!mRID) {
            console.warn(
                "[AMI-DETAIL] No se pudo determinar el mRID del medidor."
            );
            return;
        }

        const isSameDay =
            from.getFullYear() === to.getFullYear() &&
            from.getMonth() === to.getMonth() &&
            from.getDate() === to.getDate();

        const startTime = toBackendDate(from);
        const endTime = isSameDay
            ? toBackendDateEndOfDay(to)
            : toBackendDate(to);

        const payload = {
            mRID,
            startTime,
            endTime,
            ctptRatio: "yes",
            variableCimCodes: tabCimCodes,
        };

        console.log(
            "[AMI-DETAIL] >>> ConsultarVariablesPuntoSuministro (" +
            source +
            ")"
        );
        console.log("[AMI-DETAIL] base_url_api:", properties.base_url_api);
        console.log("[AMI-DETAIL] url_ws_empresa:", properties.url_ws_empresa);
        console.log("[AMI-DETAIL] pestaña usada:", currentTab);
        console.log("[AMI-DETAIL] payload enviado:", payload);

        setLoading(true);
        setError(null);
        setRawResponse(null);
        setHasConsulted(true);

        LiferayService.consultarVariablesPuntoSuministro(
            properties.base_url_api,
            payload,
            properties.url_ws_empresa
        )
            .then((resp) => {
                console.log(
                    "[AMI-DETAIL] Respuesta cruda del servicio:",
                    resp
                );

                const httpStatus = resp?.response?.status;
                const data = resp?.data || resp || {};
                console.log("[AMI-DETAIL] Data normalizada:", data);

                // Si el HTTP status NO es 200, mostramos mensaje de "no hay datos"
                if (httpStatus && httpStatus !== 200) {
                    console.warn(
                        "[AMI-DETAIL] Código HTTP no exitoso:",
                        httpStatus
                    );
                    setError(labels.noDataMessage);
                    setRawResponse(null);
                    setHasConsulted(false); // no hay datos
                    return;
                }

                // Éxito (200): guardamos data
                setRawResponse(data);
            })
            .catch((err) => {
                console.error(
                    "[AMI-DETAIL] Error consultando variables AMI:",
                    err
                );
                setError(labels.error);
                setHasConsulted(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    /**
     * Consulta validando canConsult (botón Consultar / slider).
     */
    const performConsult = (from, to, source = "botón Consultar") => {
        if (!canConsult) return;
        if (!from || !to) return;
        // aquí usamos la pestaña activa actual
        doConsult(from, to, source);
    };

    const handleConsultar = () => {
        if (!canConsult) return;

        const from = parseYMD(fromDate);
        const to = parseYMD(toDate);
        if (!from || !to) return;

        performConsult(from, to, "botón Consultar");
        // IMPORTANTE: ya NO reseteamos userChangedDates aquí,
        // para que las fechas sigan considerándose personalizadas
        // y se conserven al cambiar de pestaña.
    };

    /**
     * Inicializa el rango de fechas usando la fechaHasta del medidor
     * (selected.validTo) y 40 días hacia atrás. Llama al servicio automáticamente.
     * Solo se usa en carga inicial o cuando cambia de medidor.
     */
    const initRangeFromSelected = (source, tabIndex) => {
        if (!selected || !accessToken || !tabs.length) return;

        // Tomamos fechaHasta desde el medidor; si no viene, usamos hoy.
        let hastaRaw = selected.validTo || selected.validFrom;
        let hasta = hastaRaw ? new Date(hastaRaw) : new Date(today);

        if (Number.isNaN(hasta.getTime())) {
            hasta = new Date(today);
        }

        // Clamp a hoy y al mínimo seleccionable (24 meses)
        if (hasta > today) hasta = new Date(today);
        if (hasta < minSelectable) hasta = new Date(minSelectable);

        // 40 días antes
        let desde = addDays(hasta, -MAX_DAYS);
        if (desde < minSelectable) {
            desde = new Date(minSelectable);
        }

        const desdeStr = toYMD(desde);
        const hastaStr = toYMD(hasta);

        setFromDate(desdeStr);
        setToDate(hastaStr);
        setUserChangedDates(false); // importante: fechas por defecto

        // Ajustamos slider para todo el rango
        const ONE_DAY = 1000 * 60 * 60 * 24;
        const diff = Math.max(
            0,
            Math.round((hasta.getTime() - desde.getTime()) / ONE_DAY)
        );
        setRange([0, diff]);

        // Llamada automática al servicio (usa los cimCodes de la pestaña correspondiente)
        doConsult(desde, hasta, source, tabIndex);
    };

    const handleTabClick = (idx) => {
        setActive(idx);
        setIsHintVisible(false);

        // Si no hay rango válido o no hay fechas, no hacemos nada más
        if (!hasValidRange) return;

        const from = parseYMD(fromDate);
        const to = parseYMD(toDate);
        if (!from || !to) return;

        if (!userChangedDates) {
            // El usuario NO ha tocado fechas:
            // usamos el mismo rango actual (default) y consultamos automáticamente
            doConsult(
                from,
                to,
                "cambio de pestaña (rango por defecto)",
                idx
            );
        } else {
            // El usuario YA modificó las fechas:
            // mantenemos sus fechas, limpiamos los datos,
            // y el botón Consultar será el único que gatille el servicio.
            setHasConsulted(false);
            setRawResponse(null);
            setError(null);
        }
    };

    // Inicializar rango y hacer consulta automática al entrar al detalle
    // siempre con la pestaña 0 (Energía activa)
    useEffect(() => {
        if (selected && accessToken && tabs.length) {
            initRangeFromSelected("carga inicial", 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, accessToken, tabs.length]);

    /**
     * Handler para registrar log de exportación de Excel.
     * Se ejecuta al hacer clic en el botón de exportar.
     */
    const handleExcelExportClick = async () => {
        // Si no hay filas o no hay rango/fechas, no hacemos nada
        if (!excelRows || !excelRows.length || !fromDate || !toDate) {
            console.warn("[AMI-DETAIL] No hay datos/fechas para log de export Excel.");
            return;
        }

        const current = tabs[active] || tabs[0] || {};
        const tipoEnergia = current.label || "—";

        const medidor =
            (USE_HARDCODED_MRID ? HARDCODED_MRID : selected?.serial) || "";

        const path = getPathName();

        // Empresa: tratamos de obtener un valor razonable desde properties
        const empresa =
            properties?.codigo_empresa ||
            properties?.empresa ||
            properties?.sociedad ||
            "";

        const payloadLog = {
            medidor,
            tipoEnergia,
            fechaDesde: fromDate,
            fechaHasta: toDate,
            path,
            empresa,
        };

        console.log("[AMI-DETAIL] Enviando log de exportación Excel:", payloadLog);

        try {
            setLoading(true);

            await LiferayService.registrarAmiExportExcelLog(
                properties.base_url_api,
                payloadLog
            );

            console.log("[AMI-DETAIL] Log de exportación Excel registrado correctamente.");
        } catch (e) {
            console.error(
                "[AMI-DETAIL] Error al registrar log de exportación Excel:",
                e
            );
            // No rompemos la UI por error de log, solo lo dejamos en consola
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="card ami-detail">
            <div className="card-header d-flex align-items-center justify-content-between">
                <h2 className="m-0">{labels.title}</h2>
                <ClayButton displayType="secondary" onClick={onBack}>
                    ← {labels.backButton}
                </ClayButton>
            </div>

            <div className="card-body ami-detail__body">
                <div className="mb-2">
                    {loading && (
                        <>
                            <Loading />
                            <span>{labels.loading}</span>
                        </>
                    )}
                    {!loading && error && (
                        <span className="text-danger">{error}</span>
                    )}
                    {SHOW_SERVICE_LOG &&
                        !loading &&
                        rawResponse &&
                        !error && (
                            <span className="text-success">
                                Datos recibidos del servicio (ver consola).
                            </span>
                        )}
                </div>

                {selected && (
                    <div className="mb-3 ami-detail__header">
                        {/* Fila 1: solo botón Excel pegado a la derecha */}
                        {hasConsulted && (
                            <div className="ami-detail__excel-row">
                                <div
                                    className="ami-detail__excel-button"
                                    onClick={handleExcelExportClick}
                                >
                                    <CGE.ButtonExcel props={excelButtonProps} />
                                </div>
                            </div>
                        )}

                        {/* Fila 2: izquierda datos del cliente, derecha rango de fechas */}
                        <div className="ami-detail__main-row">
                            {/* Lado izquierdo: Número de cliente + serie + dirección */}
                            <div className="ami-detail__info">
                                {numeroCliente && (
                                    <>
                                        <strong>Número de cliente:</strong> {numeroCliente}
                                        <br />
                                    </>
                                )}
                                <strong>Número de serie:</strong> {selected.serial}
                                <br />
                                <strong>Dirección:</strong> {selected.address}
                            </div>

                            {/* Lado derecho: mensaje + fechas + botón Consultar */}
                            <div className="ami-detail__filters">
                                <div className="ami-detail__range-instruction">
                                    {labels.dateRangeInstruction}
                                </div>

                                <div
                                    className="ami-range__inputs"
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: "flex-start",
                                        marginTop: 8,
                                    }}
                                >
                                    {/* Fecha desde */}
                                    <div className="ami-range__input-group">
                                        <label
                                            className="ami-range__label"
                                            htmlFor="ami-date-from"
                                        >
                                            {labels.dateFromLabel}
                                        </label>
                                        <input
                                            id="ami-date-from"
                                            type="date"
                                            aria-label={labels.dateFromLabel}
                                            min={toYMD(minSelectable)} // hoy - 24 meses
                                            max={toYMD(today)} // hoy
                                            value={fromDate}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setFromDate(value);
                                                setUserChangedDates(true);
                                                // ajuste de toDate
                                                if (value && toDate) {
                                                    const from = parseYMD(value);
                                                    const currentTo = parseYMD(toDate);
                                                    if (from && currentTo) {
                                                        let maxAllowed = addDays(from, MAX_DAYS);
                                                        if (maxAllowed > today) {
                                                            maxAllowed = today;
                                                        }
                                                        if (
                                                            currentTo < from ||
                                                            currentTo > maxAllowed
                                                        ) {
                                                            const clamped =
                                                                currentTo < from
                                                                    ? from
                                                                    : maxAllowed;
                                                            setToDate(toYMD(clamped));
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </div>

                                    {/* Fecha hasta */}
                                    <div className="ami-range__input-group">
                                        <label
                                            className="ami-range__label"
                                            htmlFor="ami-date-to"
                                        >
                                            {labels.dateToLabel}
                                        </label>
                                        <input
                                            id="ami-date-to"
                                            type="date"
                                            aria-label={labels.dateToLabel}
                                            min={
                                                fromDate
                                                    ? fromDate
                                                    : toYMD(minSelectable)
                                            }
                                            max={toYMD(maxToDate)}
                                            value={toDate}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                const to = parseYMD(value);
                                                const from = parseYMD(fromDate);

                                                if (to && from) {
                                                    let localMax = addDays(from, MAX_DAYS);
                                                    if (localMax > today) {
                                                        localMax = today;
                                                    }

                                                    if (to < from) {
                                                        setToDate(toYMD(from));
                                                        setUserChangedDates(true);
                                                        return;
                                                    }
                                                    if (to > localMax) {
                                                        setToDate(toYMD(localMax));
                                                        setUserChangedDates(true);
                                                        return;
                                                    }
                                                }

                                                setToDate(value);
                                                setUserChangedDates(true);
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Botón CONSULTAR: solo visible cuando el usuario cambió las fechas */}
                                {userChangedDates && (
                                    <div className="mt-2 text-right">
                                        <ClayButton
                                            displayType={
                                                canConsult ? "primary" : "secondary"
                                            }
                                            disabled={!canConsult || loading}
                                            onClick={handleConsultar}
                                        >
                                            Consultar
                                        </ClayButton>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabs dinámicas */}
                {tabs.length > 0 && (
                    <>
                        <ClayTabs modern className="ami-tabs">
                            {tabs.map((tab, idx) => (
                                <ClayTabs.Item
                                    key={tab.key}
                                    active={active === idx}
                                    innerProps={{
                                        "aria-controls": `tab-${tab.key}`,
                                    }}
                                    onClick={() => handleTabClick(idx)}
                                >
                                    {tab.label}
                                </ClayTabs.Item>
                            ))}
                        </ClayTabs>

                        <div
                            role="tabpanel"
                            id={`tab-${currentTab?.key || "tab0"}`}
                            aria-labelledby={`tab-${currentTab?.key || "tab0"}`}
                            className="ami-tabpanel"
                        >
                            {/* Solo mostramos slider cuando hay rango válido y ya se consultó */}
                            {hasValidRange && hasConsulted && (
                                <div className="ami-range">
                                    <div className="ami-range__labels mt-3">
                                        <span>{sliderLeftText}</span>
                                        <span>{sliderRightText}</span>
                                    </div>

                                    <RangeWithTooltip
                                        className="ami-range__slider"
                                        min={0}
                                        max={totalDays}
                                        value={range}
                                        allowCross={false}
                                        onChange={setRange}
                                        onAfterChange={(newRange) => {
                                            setRange(newRange);
                                            if (
                                                !hasValidRange ||
                                                !hasConsulted ||
                                                !canConsult ||
                                                loading
                                            ) {
                                                return;
                                            }
                                            const from = parseYMD(fromDate);
                                            if (!from) {
                                                return;
                                            }
                                            const startOffset = Math.min(
                                                newRange[0],
                                                newRange[1]
                                            );
                                            const endOffset = Math.max(
                                                newRange[0],
                                                newRange[1]
                                            );
                                            const newFrom = addDays(
                                                from,
                                                startOffset
                                            );
                                            const newTo = addDays(
                                                from,
                                                endOffset
                                            );
                                            performConsult(
                                                newFrom,
                                                newTo,
                                                "slider de rango"
                                            );
                                        }}
                                        tipFormatter={tipFormatter}
                                        tipProps={{
                                            visible: true,
                                            placement: "top",
                                        }}
                                    />

                                    <div
                                        style={{
                                            textAlign: "right",
                                            marginTop: 6,
                                            fontSize: 12,
                                            color: "#4c5a67",
                                        }}
                                    >
                                        {daysSelected}{" "}
                                        {daysSelected === 1
                                            ? labels.daysSelectedSingular
                                            : labels.daysSelectedPlural}
                                    </div>
                                </div>
                            )}

                            {/* Gráfica solo cuando hay rango válido, se consultó y hay datos */}
                            {hasValidRange &&
                                hasConsulted &&
                                series.length > 0 && (
                                    <>
                                        <div className="ami-chart__head">
                                            <div className="ami-chart__title" style={{ visibility: "hidden" }}>
                                                {labels.chartTitle}
                                            </div>
                                            <div className="ami-chart__hint">
                                                <button
                                                    type="button"
                                                    className="ami-chart__hint-trigger"
                                                    onMouseEnter={() =>
                                                        setIsHintVisible(true)
                                                    }
                                                    onMouseLeave={() =>
                                                        setIsHintVisible(false)
                                                    }
                                                    onFocus={() =>
                                                        setIsHintVisible(true)
                                                    }
                                                    onBlur={() =>
                                                        setIsHintVisible(false)
                                                    }
                                                >
                                                    <span className="ami-chart__hint-text">
                                                        {labels.chartHintPrefix}
                                                        {chartHintMetric}
                                                        {
                                                            labels.chartHintSuffix
                                                        }
                                                    </span>
                                                    <span className="ami-chart__hint-icon">
                                                        ?
                                                    </span>
                                                </button>
                                                {isHintVisible &&
                                                    chartHintTooltip && (
                                                        <div className="ami-chart__hint-tooltip">
                                                            {chartHintTooltip}
                                                        </div>
                                                    )}
                                            </div>
                                        </div>

                                        <div className="ami-chart__container">
                                            <ReactApexChart
                                                type="line"
                                                height={260}
                                                series={series}
                                                options={options}
                                            />
                                        </div>
                                    </>
                                )}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
